import Loading from './../misc/Loading';
import React, {useEffect, useState} from 'react';
import assetModel from './../../../../model/asset';
import MediaComponent from './Media';
import ValidationError from '../../../../model/error/ValidationError';
import {NavLink as Link, useParams} from 'react-router-dom';
import {enrichTags} from '../../../misc/utils';
import {useTags} from '../../../../providers/TagsProvider';

const errorToMessage = (error) => {
  console.error(error);
  if (error instanceof ValidationError) {
    return 'Mediaelementet validerer ikke og kan dermed ikke publiseres. Sjekk at alle påkrevede felter er fylt ut med gyldige verdier og prøv igjen.';
  }

  return 'En ukjent feil inntraff ved behandling av mediaelementet og handlingen kunne dermed ikke fullføres.';
};

const MediaContainer = () => {
  const [modified, setModified] = useState(false);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState(null);
  const [mediaItem, setMediaItem] = useState(null);
  const [allTags, setAllTags] = useState([]);
  const [linkedEntries, setLinkedEntries] = useState([]);
  const {getTags, refreshTags, lastFetched} = useTags();
  const {mediaId} = useParams();

  const getData = async (mediaId) => {
    try {
      setWorking(true);
      const [tags, mediaItem, linkedEntries] = await Promise.all([
        getTags(),
        assetModel.getById(mediaId),
        assetModel.getLinkedEntries(mediaId)
      ]);

      document.title = `Admin - Media - ${mediaItem.title}`;

      mediaItem.tags = enrichTags(tags, mediaItem.tags);

      setLinkedEntries(linkedEntries);
      setAllTags(tags);
      setMediaItem(mediaItem);
    } catch (e) {
      setError(errorToMessage(e));
    } finally {
      setWorking(false);
    }
  };

  useEffect(() => {
    if (mediaId) {
      getData(mediaId);
    }
  }, [mediaId]);

  useEffect(() => {
    // Create an anonymous function and immediately call it to make use of await.
    // This is done because useEffect cant be async yet, so this is a workaround.
    (async () => {
      const tags = await getTags();
      setAllTags(tags);
    })();
  }, [lastFetched]);


  const handleSave = async () => {
    setError(null);
    setModified(false);

    try {
      setWorking(true);

      const updatedMediaItem = await assetModel.save(mediaItem);
      updatedMediaItem.tags = enrichTags(allTags, updatedMediaItem.tags);

      setMediaItem(updatedMediaItem);
    } catch (e) {
      setError(errorToMessage(e));
    } finally {
      setWorking(false);
    }
  };

  const handleArchive = async () => {
    setError(null);

    try {
      setWorking(true);

      const updatedMediaItem = await assetModel.archive(mediaItem.id);
      updatedMediaItem.tags = enrichTags(allTags, updatedMediaItem.tags);

      setMediaItem(updatedMediaItem);
    } catch (e) {
      setError(errorToMessage(e));
    } finally {
      setWorking(false);
    }
  };

  const handleUnarchive = async () => {
    setError(null);

    try {
      setWorking(true);

      const updatedMediaItem = await assetModel.unarchive(mediaItem.id);
      updatedMediaItem.tags = enrichTags(allTags, updatedMediaItem.tags);

      setMediaItem(updatedMediaItem);
    } catch (e) {
      setError(errorToMessage(e));
    } finally {
      setWorking(false);
    }
  };

  const handleChange = ({mediaItem: updatedMediaItem}) => {
    setModified(true);

    setMediaItem(updatedMediaItem);
  };

  if (!mediaItem) {
    return (
      <section className="campaign mf-padding-top-20">
        <ol className="breadcrumb">
          <li><Link to="/">Forsiden</Link></li>
          <li><Link to="/media/">Mediautforsker</Link></li>
          <li className="active">...</li>
        </ol>
        <Loading title="Mediautforsker" text="Laster mediaelement" />
      </section>
    );
  }

  return (
    <MediaComponent
      mediaItem={mediaItem}
      linkedEntries={linkedEntries}
      tags={allTags}
      refreshTags={refreshTags}
      error={error}
      modified={modified}
      working={working}
      onChange={handleChange}
      onSave={handleSave}
      onArchive={mediaItem.id ? handleArchive : null}
      onUnarchive={mediaItem.id ? handleUnarchive : null}
    />
  );
};

export default MediaContainer;
